import { useMemo } from "react";
import { useGetMultipleQuery } from "../../../../services/historicalData";
import { addDays, endOfDay, roundToNearestMinutes } from "date-fns";
import { useGetMatchQuery } from "../../../../services/objects";


export function useGetRoomsTempertures(parentId: string, resourceGroupId: string) {

    const times = useMemo(() => ({
        start: addDays(new Date(), -7).toISOString(),
        startLastWeek: addDays(new Date(), -14).toISOString(),
        end: endOfDay(roundToNearestMinutes(new Date(), { nearestTo: 15 })).toISOString(),
        endLastWeek: addDays(endOfDay(roundToNearestMinutes(new Date(), { nearestTo: 15 })), -7).toISOString(),
    }), [])

    const { data: roomTempTwins, isLoading: isLoadingTwins, isSuccess: isSuccessTwins } = useGetMatchQuery(
        { objectId: parentId, resourceGroupId: resourceGroupId, hops: '..5', relationship: 'Contains', modelId: "dtmi:AutomationApps:Devices:Sensors:TemperatureSensor;1" }
        , { skip: !parentId || !resourceGroupId });

    const queryItems = useMemo(() => roomTempTwins?.map(r => ({ objectId: r.id, resourceGroupId: r.resourceGroupId, propertyName: 'Value' })) ?? [], [roomTempTwins]);
    const skip = queryItems.length <= 0;
    const { data, isLoading, isSuccess, error, isError, isFetching } = useGetMultipleQuery({
        cmd: {
            start: times.start,
            end: times.end,
            items: [...queryItems]
        },
        summarizeBy: '1d'
    }, { skip: skip })
    // last week
    const { data: dataLastWeek, isError: isError2, error: error2 } = useGetMultipleQuery({
        cmd: {
            start: times.startLastWeek,
            end: times.endLastWeek,
            items: [...queryItems]
        },
        summarizeBy: '1d'
    }, { skip: skip })

    const currentWeek = useMemo(() => {
        if (isError || !data)
            return { minValue: 0, avgValue: 0, maxValue: 0 };

        const values = data?.flatMap(v => v.data).filter(v => v !== undefined);
        if (!values)
            return { minValue: 0, avgValue: 0, maxValue: 0 };

        const maxValue = Math.max(...values?.map(v => v.maxValue > 120 ? 0 : v.maxValue) ?? [0]); 
        const minValue = Math.min(...values?.map(v => v.minValue) ?? [0]);
        const avgValue = Math.round(average(values?.filter(v => v.avgValue < 120).map(v => v.avgValue) ?? [0]) * 10) / 10;
        return { minValue, avgValue, maxValue }
    }, [data,isError]);

    const lastWeek = useMemo(() => {
        if (isError2 || !dataLastWeek)
            return { minValue: 0, avgValue: 0, maxValue: 0 };

        const values = dataLastWeek?.flatMap(v => v.data).filter(v => v !== undefined);
        if (!values)
            return { minValue: 0, avgValue: 0, maxValue: 0 };

        const maxValue = Math.max(...values?.map(v => v.maxValue> 120 ? 0 : v.maxValue) ?? [0]); 
        const minValue = Math.min(...values?.map(v => v.minValue) ?? [0]);
        const avgValue = Math.round(average(values?.filter(v => v.avgValue < 120).map(v => v.avgValue) ?? [0]) * 10) / 10;
        return { minValue, avgValue, maxValue }
    }, [dataLastWeek,isError2]);

    return {
        hasItems: !skip,
        currentWeek: currentWeek,
        lastWeek: lastWeek,
        isLoading: isLoadingTwins || isLoading,
        isSuccess: isSuccessTwins || isSuccess,
        isError: isError || isError2,
        error: error || error2
    }
}

const average = arr => arr.reduce((acc, v, i, a) => (acc + v / a.length), 0);
